/**
 * AwsMappingTableRows.js
 *
 * This component handles the rendering and editing of individual rows in the AWS Mapping table.
 * It provides functionality for editing AWS mapping records and managing their state.
 *
 * Key Features:
 * - Row editing with save/cancel functionality
 * - Integration with AWS mapping configuration
 * - Error handling and status messages
 * - State management for editing operations
 *
 * Props:
 * @param {Object} data - The row data containing AWS mapping information
 * @param {boolean} editMode - Whether the table is in edit mode
 * @param {boolean} deleteMode - Whether the table is in delete mode
 * @param {Array} selectedRows - Array of selected row IDs
 * @param {Function} setselectedRows - Function to update selected rows
 * @param {Function} refetchRows - Function to refresh the data
 * @param {Function} setMessage - Function to set status messages
 * @param {boolean} cancelEditMode - Flag to cancel edit mode
 * @param {Object} ref - React ref for infinite scrolling
 */

// FlagTableRows.js
import React, { useState, forwardRef, useEffect } from "react";
import {
  TableCell,
  TableRow,
  Checkbox,
  Button,
  TextField,
  Select,
  MenuItem,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import { updateFlag } from "../../services/api";
import moment from "moment";
import { awsMappingConfig } from "../../config/formConfigs/awsMappingConfig";

const AwsMappingTableRows = forwardRef(
  (
    {
      data,
      editMode,
      deleteMode,
      selectedRows,
      setselectedRows,
      refetchRows,
      setMessage,
      cancelEditMode,
    },
    ref
  ) => {
    const [editingId, setEditingId] = useState(null);
    const [editedRow, seteditedRow] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    /**
     * Handles the edit action for a row
     * @param {Object} data - The row data to edit
     */
    const handleEdit = (data) => {
      setEditingId(data.id);
      seteditedRow({
        ...data,
      });
    };

    /**
     * Saves the edited row data to the server
     * Handles API calls and updates UI state
     */
    const handleSave = async () => {
      setIsLoading(true);
      try {
        const formattedRow = {
          ...editedRow,
        };
        console.log(formattedRow);

        await updateFlag(formattedRow.id, formattedRow);
        setEditingId(null);
        refetchRows();
        setMessage({
          text: "Mapping updated successfully",
          severity: "success",
          open: true,
        });
      } catch (error) {
        console.error("Error updating Mapping:", error);
        setMessage({
          text: error.message || "Error updating Mapping",
          severity: "error",
          open: true,
        });
      } finally {
        setIsLoading(false);
      }
    };

    /**
     * Cancels the current edit operation
     * Resets the editing state
     */
    const handleCancel = () => {
      setEditingId(null);
      seteditedRow({});
    };
    // Use useEffect to listen for changes in cancelEditMode
    useEffect(() => {
      if (cancelEditMode) {
        handleCancel();
      }
    }, [cancelEditMode]);

    /**
     * Handles changes to form fields
     * @param {Event} e - The change event
     * @param {string} field - The field being changed
     */
    const handleChange = (e, field) => {
      seteditedRow({ ...editedRow, [field]: e.target.value });
    };

    /**
     * Toggles row selection
     * @param {string} id - The ID of the row to select/deselect
     */
    const handleSelectedRow = (id) => {
      setselectedRows((prev) =>
        prev.includes(id)
          ? prev.filter((dataId) => dataId !== id)
          : [...prev, id]
      );
    };

    return (
      <TableRow ref={ref}>
        {editMode && (
          <TableCell>
            {editingId === data.id ? (
              <>
                <Button
                  onClick={handleSave}
                  startIcon={<SaveIcon />}
                  size="small"
                >
                  Save
                </Button>
                <Button
                  onClick={handleCancel}
                  startIcon={<CancelIcon />}
                  size="small"
                >
                  Cancel
                </Button>
              </>
            ) : (
              <Button
                onClick={() => handleEdit(data)}
                startIcon={<EditIcon />}
                size="large"
              />
            )}
          </TableCell>
        )}
        <TableCell>{data.id}</TableCell>
        <TableCell>{data.imei}</TableCell>
        <TableCell>{data.uid}</TableCell>
        <TableCell>
          {data.metadata_mapping_id !== null ? data.metadata_mapping_id : "-"}
        </TableCell>

        <TableCell>
          {data.latest_change_history_id !== null
            ? data.latest_change_history_id
            : "-"}
        </TableCell>
      </TableRow>
    );
  }
);

export default AwsMappingTableRows;
